import React, { useEffect, useMemo } from "react";
import type { CreateOptionsType, EmblaOptionsType } from "embla-carousel";
import { DotButton, useDotButton } from "./EmblaCarouselDotButton";
import {
  PrevButton,
  NextButton,
  usePrevNextButtons,
} from "./EmblaCarouselArrowButtons";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import "./embla.css";

type Options = {
  playOnInit?: boolean;
  delay?: number;
  draggable?: boolean;
  stopOnInteraction?: boolean;
  stopOnMouseEnter?: boolean;
};

type PropType = {
  className?: string;
  slides: React.ReactElement[];
  options?: EmblaOptionsType;
  userOptions?: Partial<CreateOptionsType<Options>> | undefined;
  arrows?: boolean;
  dots?: boolean;
};

const EmblaCarousel: React.FC<PropType> = (props) => {
  const { className, slides, options, userOptions } = props;

  const autoplayPlugin = useMemo(() => {
    if (userOptions?.playOnInit === false) return null;
    return Autoplay({
      delay: userOptions?.delay ?? 20000,
      stopOnInteraction: userOptions?.stopOnInteraction ?? true,
      stopOnMouseEnter: userOptions?.stopOnMouseEnter ?? true,
    });
  }, [userOptions]);

  const plugins = useMemo(
    () => (autoplayPlugin ? [autoplayPlugin] : []),
    [autoplayPlugin],
  );

  const [emblaRef, emblaApi] = useEmblaCarousel(options, plugins);

  useEffect(() => {
    if (!emblaApi) return;

    const reinit = () => {
      const current = emblaApi.selectedScrollSnap();
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          emblaApi.reInit();
          emblaApi.scrollTo(current, true);
        });
      });
    };

    window.addEventListener("popstate", reinit);

    const rootNode = emblaApi.rootNode();
    const ro = new ResizeObserver(() => reinit());
    ro.observe(rootNode);

    setTimeout(reinit, 0);

    return () => {
      window.removeEventListener("popstate", reinit);
      ro.disconnect();
    };
  }, [emblaApi]);

  const { selectedIndex, scrollSnaps, onDotButtonClick } =
    useDotButton(emblaApi);

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(emblaApi);

  return (
    <section className={(className ?? "") + " embla relative w-full m-auto"}>
      <div className="embla__viewport" ref={emblaRef}>
        <div className="embla__container">
          {slides.map((content, index) => (
            <div className="embla__slide" key={index}>
              {content}
            </div>
          ))}
        </div>
      </div>

      <div className="absolute flex justify-between top-1/2 -translate-y-1/2 w-[calc(100%+8rem)] -left-16">
        {props.arrows === true &&
          slides.length > 1 &&
          !(prevBtnDisabled && nextBtnDisabled) && (
            <>
              <PrevButton
                onClick={onPrevButtonClick}
                disabled={prevBtnDisabled}
              />
              <NextButton
                onClick={onNextButtonClick}
                disabled={nextBtnDisabled}
              />
            </>
          )}
      </div>

      <div className="absolute bottom-0 w-full">
        {props.dots === true && (
          <div className="flex justify-center items-end h-full">
            {scrollSnaps.map((_, index) => (
              <DotButton
                key={index}
                onClick={() => onDotButtonClick(index)}
                className={"embla__dot after:transition-all ".concat(
                  index === selectedIndex ? " embla__dot--selected" : "",
                )}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default EmblaCarousel;
